import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { CalendarPlus, Trash2, XCircle } from "lucide-react";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";
import { NoStaffNote } from "@/components/common/NoStaffNote";

const APPT_TYPES = ["Client Visit", "Internal Audit", "Management Review", "Certification Audit", "Consultation", "Training", "Internal Meeting", "Other"];
const REMINDERS = [
  { label: "None", value: -1 },
  { label: "15 minutes before", value: 15 },
  { label: "1 hour before", value: 60 },
  { label: "4 hours before", value: 240 },
  { label: "24 hours before (default)", value: 1440 },
  { label: "3 days before", value: 4320 },
  { label: "1 week before", value: 10080 },
];

const empty = {
  title: "", client: "", date: new Date().toISOString().slice(0, 10),
  time: "09:00", end_time: "10:00", location: "", consultant: "",
  type: "Client Visit", notes: "", reminder_minutes_before: 1440,
  email_reminder: false, status: "Confirmed",
};

export default function AppointmentModal({ open, initial, consultants = [], onOpenChange, onChanged }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial?.id;

  useEffect(() => {
    if (open) setForm({ ...empty, ...(initial || {}) });
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      if (isEdit) {
        await endpoints.updateAppointment(initial.id, form);
        toast.success("Appointment updated");
      } else {
        await endpoints.createAppointment(form);
        toast.success("Appointment created");
      }
      onChanged?.();
    } catch {
      toast.error("Could not save appointment");
    } finally {
      setSaving(false);
    }
  };

  const cancelAppt = async () => {
    setSaving(true);
    try {
      await endpoints.updateAppointment(initial.id, { status: "Cancelled" });
      toast.success("Appointment cancelled");
      onChanged?.();
    } catch { toast.error("Could not cancel"); } finally { setSaving(false); }
  };

  const deleteAppt = async () => {
    setSaving(true);
    try {
      await endpoints.deleteAppointment(initial.id);
      toast.success("Appointment deleted");
      onChanged?.();
    } catch { toast.error("Could not delete"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="modal-appointment" className="sm:max-w-[560px] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl brand-gradient text-white flex items-center justify-center shadow-md shadow-indigo-500/25">
              <CalendarPlus className="h-4.5 w-4.5" />
            </div>
            <div>
              <DialogTitle>{isEdit ? "Edit appointment" : "New appointment"}</DialogTitle>
              <DialogDescription>{isEdit ? "Update, move or cancel." : "Book a visit, audit or review."}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3.5 pt-2">
          <div>
            <Label htmlFor="a-title">Title</Label>
            <Input data-testid="appt-title-input" id="a-title" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. ISO 9001 audit with Northwind" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="a-client">Client / Organization</Label>
              <Input data-testid="appt-client-input" id="a-client" value={form.client} onChange={(e) => set("client", e.target.value)} placeholder="Client name" />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger data-testid="appt-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {APPT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="a-date">Date</Label>
              <Input data-testid="appt-date-input" id="a-date" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="a-start">Start</Label>
              <Input data-testid="appt-time-input" id="a-start" type="time" value={form.time} onChange={(e) => set("time", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="a-end">End</Label>
              <Input data-testid="appt-end-time-input" id="a-end" type="time" value={form.end_time || ""} onChange={(e) => set("end_time", e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="a-loc">Location</Label>
              <Input data-testid="appt-location-input" id="a-loc" value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Site, office or URL" />
            </div>
            <div>
              <Label>Assigned to</Label>
              <Select value={form.consultant || "unassigned"} onValueChange={(v) => set("consultant", v === "unassigned" ? "" : v)}>
                <SelectTrigger data-testid="appt-consultant-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {consultants.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <NoStaffNote list={consultants} testid="appt-no-staff-note" />
            </div>
          </div>

          <div>
            <Label htmlFor="a-notes">Notes</Label>
            <Textarea data-testid="appt-notes-input" id="a-notes" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Agenda, scope, prep notes..." />
          </div>

          <div className="grid grid-cols-2 gap-3 items-end">
            <div>
              <Label>Reminder</Label>
              <Select value={String(form.reminder_minutes_before)} onValueChange={(v) => set("reminder_minutes_before", Number(v))}>
                <SelectTrigger data-testid="appt-reminder-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REMINDERS.map((r) => <SelectItem key={r.value} value={String(r.value)}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-xl border border-slate-200 dark:border-white/10 bg-white/60 dark:bg-slate-900/40 px-3 py-2.5">
              <div>
                <div className="text-sm font-semibold">Email consultant</div>
                <div className="text-[11px] text-slate-500">Send the reminder by email too</div>
              </div>
              <Switch data-testid="appt-email-reminder-switch" checked={!!form.email_reminder} onCheckedChange={(v) => set("email_reminder", v)} />
            </div>
          </div>

          {isEdit && form.status === "Cancelled" && (
            <div className="rounded-xl bg-rose-50 dark:bg-rose-500/15 border border-rose-200 dark:border-rose-500/25 text-rose-700 dark:text-rose-300 text-xs px-3 py-2">
              This appointment is cancelled.
            </div>
          )}

          <DialogFooter className="flex-wrap gap-2">
            {isEdit && (
              <>
                <Button data-testid="appt-delete-button" type="button" variant="ghost" className="text-rose-600 hover:text-rose-700 mr-auto" onClick={deleteAppt} disabled={saving}>
                  <Trash2 className="h-4 w-4 mr-1" /> Delete
                </Button>
                {form.status !== "Cancelled" && (
                  <Button data-testid="appt-cancel-button" type="button" variant="outline" onClick={cancelAppt} disabled={saving}>
                    <XCircle className="h-4 w-4 mr-1" /> Cancel appointment
                  </Button>
                )}
              </>
            )}
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
            <Button data-testid="appt-submit-button" type="submit" disabled={saving} className="brand-gradient text-white">
              {saving ? "Saving..." : isEdit ? "Save changes" : "Create appointment"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
